import { forwardRef, KeyboardEvent, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Tooltip } from 'antd'
import { domToTg, tgToEditorHtml } from '../utils/tgFormat'

// Редактор текста с форматированием как в Telegram: выделил фрагмент - нажал
// кнопку - формат сразу виден. Значение хранится в «формате Telegram»
// (utils/tgFormat.ts), его же сервер превращает в отметки форматирования при
// отправке. Один редактор на всё: шаблоны первого сообщения, быстрые шаблоны,
// поле ввода в чате.

export interface TgRichEditorHandle {
    focus: () => void
    // Вставить форматированный фрагмент (например, шаблон) в позицию курсора.
    insertTg: (tg: string) => void
    insertText: (text: string) => void
    clear: () => void
}

type ToolbarMode = 'top' | 'selection' | 'none'

interface TgRichEditorProps {
    // Необязательны, чтобы редактор можно было положить в Form.Item antd:
    // форма сама передаёт value и onChange.
    value?: string
    onChange?: (value: string) => void
    placeholder?: string
    minRows?: number
    maxRows?: number
    // top - панель всегда над полем (шаблоны); selection - появляется, когда
    // выделен текст (поле ввода в чате, где место дорого).
    toolbar?: ToolbarMode
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
    className?: string
    disabled?: boolean
}

type FormatKey = 'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'quote' | 'spoiler' | 'clear'

const BUTTONS: Array<{ key: FormatKey; label: ReactNode; title: string }> = [
    { key: 'bold', label: <b>Ж</b>, title: 'Жирный (Ctrl+B)' },
    { key: 'italic', label: <i>К</i>, title: 'Курсив (Ctrl+I)' },
    { key: 'underline', label: <u>Ч</u>, title: 'Подчёркнутый (Ctrl+U)' },
    { key: 'strike', label: <s>З</s>, title: 'Зачёркнутый (Ctrl+Shift+X)' },
    { key: 'code', label: <span className="tg-editor-mono">{'</>'}</span>, title: 'Моноширинный (Ctrl+Shift+M)' },
    { key: 'quote', label: '❝', title: 'Цитата (Ctrl+Shift+.)' },
    { key: 'spoiler', label: '👁', title: 'Скрытый текст (Ctrl+Shift+P)' },
    { key: 'clear', label: '⌫', title: 'Убрать форматирование' },
]

const LINE_HEIGHT = 22

function unwrap(element: Element) {
    const parent = element.parentNode
    if (!parent) return
    while (element.firstChild) parent.insertBefore(element.firstChild, element)
    parent.removeChild(element)
}

function closestIn(node: Node | null, root: HTMLElement, match: (element: Element) => boolean): Element | null {
    let current: Node | null = node
    while (current && current !== root) {
        if (current.nodeType === Node.ELEMENT_NODE && match(current as Element)) return current as Element
        current = current.parentNode
    }
    return null
}

const isSpoiler = (element: Element) => element.tagName === 'SPAN' && element.classList.contains('tg-spoiler')
const isCode = (element: Element) => element.tagName === 'CODE'
const isQuote = (element: Element) => element.tagName === 'BLOCKQUOTE'

const TgRichEditor = forwardRef<TgRichEditorHandle, TgRichEditorProps>(function TgRichEditor(
    { value = '', onChange, placeholder, minRows = 3, maxRows = 12, toolbar = 'top', onKeyDown, className, disabled },
    ref,
) {
    const editorRef = useRef<HTMLDivElement>(null)
    const lastValue = useRef<string | null>(null)
    const savedRange = useRef<Range | null>(null)
    const [isEmpty, setIsEmpty] = useState(!value)
    const [hasSelection, setHasSelection] = useState(false)
    const [active, setActive] = useState<Partial<Record<FormatKey, boolean>>>({})

    const emit = useCallback(() => {
        const root = editorRef.current
        if (!root) return
        const next = domToTg(root)
        lastValue.current = next
        setIsEmpty(!next)
        onChange?.(next)
    }, [onChange])

    // Значение поменяли снаружи (очистка после отправки, правка сообщения,
    // загрузка шаблона) - переписываем содержимое. Собственные изменения
    // редактора сюда не попадают, иначе курсор прыгал бы в начало.
    useEffect(() => {
        const root = editorRef.current
        if (!root || value === lastValue.current) return
        root.innerHTML = tgToEditorHtml(value || '')
        lastValue.current = value || ''
        setIsEmpty(!value)
    }, [value])

    const selectionInside = useCallback((): Range | null => {
        const root = editorRef.current
        const selection = window.getSelection()
        if (!root || !selection || !selection.rangeCount) return null
        const range = selection.getRangeAt(0)
        return root.contains(range.commonAncestorContainer) ? range : null
    }, [])

    // Кнопки панели забирают фокус - запоминаем выделение, чтобы применить
    // формат к нему, а не к пустому месту.
    useEffect(() => {
        const onSelectionChange = () => {
            const range = selectionInside()
            if (range) {
                savedRange.current = range.cloneRange()
                setHasSelection(!range.collapsed)
                try {
                    setActive({
                        bold: document.queryCommandState('bold'),
                        italic: document.queryCommandState('italic'),
                        underline: document.queryCommandState('underline'),
                        strike: document.queryCommandState('strikeThrough'),
                        code: Boolean(editorRef.current && closestIn(range.startContainer, editorRef.current, isCode)),
                        quote: Boolean(editorRef.current && closestIn(range.startContainer, editorRef.current, isQuote)),
                        spoiler: Boolean(editorRef.current && closestIn(range.startContainer, editorRef.current, isSpoiler)),
                    })
                } catch {
                    setActive({})
                }
            } else if (document.activeElement !== editorRef.current) {
                setHasSelection(false)
            }
        }
        document.addEventListener('selectionchange', onSelectionChange)
        return () => document.removeEventListener('selectionchange', onSelectionChange)
    }, [selectionInside])

    const restoreSelection = useCallback(() => {
        const root = editorRef.current
        if (!root) return
        root.focus()
        const selection = window.getSelection()
        if (!selection) return
        if (savedRange.current && root.contains(savedRange.current.commonAncestorContainer)) {
            selection.removeAllRanges()
            selection.addRange(savedRange.current)
            return
        }
        // Курсора в поле ещё не было - ставим в конец.
        const range = document.createRange()
        range.selectNodeContents(root)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
    }, [])

    const wrapSelection = (makeWrapper: () => HTMLElement, match: (element: Element) => boolean) => {
        const root = editorRef.current
        const range = selectionInside()
        if (!root || !range) return
        const existing = closestIn(range.commonAncestorContainer, root, match)
        if (existing) {
            unwrap(existing)
            return
        }
        if (range.collapsed) return
        const wrapper = makeWrapper()
        wrapper.appendChild(range.extractContents())
        // Внутри не оставляем такие же обёртки - иначе снять формат не выйдет.
        wrapper.querySelectorAll('*').forEach((element) => {
            if (match(element)) unwrap(element)
        })
        range.insertNode(wrapper)
        const selection = window.getSelection()
        if (selection) {
            const next = document.createRange()
            next.selectNodeContents(wrapper)
            selection.removeAllRanges()
            selection.addRange(next)
        }
    }

    const toggleQuote = () => {
        const root = editorRef.current
        const range = selectionInside()
        if (!root || !range) return
        const existing = closestIn(range.commonAncestorContainer, root, isQuote)
        if (existing) {
            unwrap(existing)
            return
        }
        const quote = document.createElement('blockquote')
        if (range.collapsed) {
            quote.appendChild(document.createElement('br'))
            range.insertNode(quote)
            const selection = window.getSelection()
            const next = document.createRange()
            next.setStart(quote, 0)
            next.collapse(true)
            selection?.removeAllRanges()
            selection?.addRange(next)
            return
        }
        quote.appendChild(range.extractContents())
        range.insertNode(quote)
    }

    const clearFormatting = () => {
        const root = editorRef.current
        const range = selectionInside()
        if (!root || !range) return
        document.execCommand('removeFormat')
        root.querySelectorAll('code, span.tg-spoiler, blockquote, a').forEach((element) => {
            if (range.intersectsNode(element)) unwrap(element)
        })
    }

    const apply = (key: FormatKey) => {
        if (disabled) return
        restoreSelection()
        try {
            document.execCommand('styleWithCSS', false, 'false')
        } catch {
            // старые браузеры - не страшно
        }
        switch (key) {
            case 'bold':
                document.execCommand('bold')
                break
            case 'italic':
                document.execCommand('italic')
                break
            case 'underline':
                document.execCommand('underline')
                break
            case 'strike':
                document.execCommand('strikeThrough')
                break
            case 'code':
                wrapSelection(() => document.createElement('code'), isCode)
                break
            case 'spoiler':
                wrapSelection(() => {
                    const span = document.createElement('span')
                    span.className = 'tg-spoiler'
                    span.setAttribute('data-tg', 'spoiler')
                    return span
                }, isSpoiler)
                break
            case 'quote':
                toggleQuote()
                break
            case 'clear':
                clearFormatting()
                break
        }
        emit()
    }

    useImperativeHandle(ref, () => ({
        focus: () => restoreSelection(),
        insertTg: (tg: string) => {
            restoreSelection()
            document.execCommand('insertHTML', false, tgToEditorHtml(tg))
            emit()
        },
        insertText: (text: string) => {
            restoreSelection()
            document.execCommand('insertText', false, text)
            emit()
        },
        clear: () => {
            if (editorRef.current) editorRef.current.innerHTML = ''
            savedRange.current = null
            emit()
        },
    }), [emit, restoreSelection])

    // Перенос строки вставляем сами, тегом <br>. Встроенная команда браузера в
    // разных режимах вставляет то тег, то невидимый символ переноса, и в конце
    // строки перенос терялся.
    const insertLineBreak = () => {
        const root = editorRef.current
        const range = selectionInside()
        if (!root || !range) return
        range.deleteContents()
        const br = document.createElement('br')
        range.insertNode(br)

        // Если после переноса в поле ничего нет, браузер не покажет пустую
        // строку - нужен второй <br>, на который встанет курсор.
        const after = document.createRange()
        after.setStartAfter(br)
        after.setEnd(root, root.childNodes.length)
        const hasContentAfter = after.toString().length > 0 || after.cloneContents().querySelector('br') !== null
        if (!hasContentAfter) {
            br.parentNode?.insertBefore(document.createElement('br'), br.nextSibling)
        }

        const selection = window.getSelection()
        const next = document.createRange()
        next.setStartAfter(br)
        next.collapse(true)
        selection?.removeAllRanges()
        selection?.addRange(next)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(event)
        if (event.defaultPrevented) return
        const mod = event.ctrlKey || event.metaKey
        if (mod && event.shiftKey) {
            const code = event.code
            const shortcut: FormatKey | null = code === 'KeyX' ? 'strike'
                : code === 'KeyM' ? 'code'
                    : code === 'KeyP' ? 'spoiler'
                        : code === 'Period' ? 'quote'
                            : null
            if (shortcut) {
                event.preventDefault()
                apply(shortcut)
                return
            }
        }
        if (event.key === 'Enter') {
            // Перенос строки вместо нового блока: так проще и разметка чище.
            event.preventDefault()
            insertLineBreak()
            emit()
        }
    }

    const showToolbar = toolbar === 'top' || (toolbar === 'selection' && hasSelection)

    const bar = (
        <div className="tg-editor-toolbar" onMouseDown={(event) => event.preventDefault()}>
            {BUTTONS.map((button) => (
                <Tooltip key={button.key} title={button.title} mouseEnterDelay={0.4}>
                    <button
                        type="button"
                        className={active[button.key] ? 'tg-editor-btn is-active' : 'tg-editor-btn'}
                        disabled={disabled}
                        onClick={() => apply(button.key)}
                    >
                        {button.label}
                    </button>
                </Tooltip>
            ))}
        </div>
    )

    return (
        <div className={['tg-editor', toolbar === 'selection' ? 'tg-editor-inline' : '', className || ''].filter(Boolean).join(' ')}>
            {showToolbar && bar}
            <div
                ref={editorRef}
                className="tg-editor-content"
                contentEditable={!disabled}
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                data-placeholder={placeholder || ''}
                data-empty={isEmpty ? 'true' : 'false'}
                style={{ minHeight: minRows * LINE_HEIGHT + 12, maxHeight: maxRows * LINE_HEIGHT + 12 }}
                onInput={emit}
                onBlur={emit}
                onKeyDown={handleKeyDown}
                onPaste={(event) => {
                    // Вставляем только текст: чужая разметка из браузера или
                    // Word в Telegram всё равно не уйдёт, а поле засорит.
                    event.preventDefault()
                    const text = event.clipboardData.getData('text/plain')
                    document.execCommand('insertText', false, text)
                    emit()
                }}
            />
        </div>
    )
})

export default TgRichEditor
